import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  clampOffsetMinutes,
  clockTimeForOffset,
  coverageDurationForEnd,
  formatDuration,
  formatTimeOfDay,
  minutesSinceMidnight,
  offsetForClockTime,
  parseTimeOfDay,
  type CoverageAnnotation,
  type ResolvedCoverageAnnotation,
} from '@vendorops/shared';
import {
  DAY_MINUTES,
  MIN_BLOCK_PX,
  PX_PER_MINUTE,
  anchorScrollTop,
  clampDayIndex,
  clippedBox,
  dayIndexForOffset,
  layout,
  offsetForClockTimeOnDay,
  offsetForTop,
  snapMinutes,
  topForOffset,
  type TimelineLayout,
} from '@/lib/timeline-geometry';
import {
  nudgedOffset,
  useCoverageSweep,
  useTimelineDrag,
  type CoverageSpan,
  type DragKind,
  type ScrollWindow,
  type TimelineDragState,
  type TimelineRetime,
} from '@/hooks/use-timeline-drag';
import { Input } from '@/components/ui/input';
import TimelineBlock, { type TimelineGrabHandlers } from '@/components/TimelineBlock';
import TimelineDateSelector from '@/components/TimelineDateSelector';
import {
  EDGE_PX,
  EdgeHandles,
  ReadoutPlate,
  type TimelineReadout,
} from '@/components/TimelineGrab';
import CoverageTrack, {
  CoverageGround,
  DEFAULT_WINDOW_MINUTES,
  MIN_WINDOW_MINUTES,
  TRACK_REM,
  type CoveragePlacement,
  type CoverageRetime,
  type CoverageTags,
  type TrackCoverage,
} from '@/components/CoverageTrack';
import {
  type TimelineItemDraft,
  type TimelineItemPlacement,
  type TimelineItemView,
} from '@/components/TimelineItem';

/**
 * The day, against the clock.
 *
 * There is no sort column on a timeline item: order *is* time. So the axis
 * cannot be a list that happens to be chronological — a block's vertical
 * position has to be its time, or a drag would have to invent an offset the
 * planner never asked for. Every number on screen comes from `layout()`; this
 * file turns those numbers into elements and decides nothing else about them.
 *
 * Nothing measures the DOM. `layout()` is pure, the positions are inline
 * styles, and no `getBoundingClientRect` is read to render — which is what lets
 * this be tested in a DOM with no layout engine at all, and what will let a
 * drag map a pointer to a minute from an origin handed in rather than from a
 * box read halfway through a gesture.
 *
 * Every *vertical* number comes from `layout()`. Horizontally the axis is
 * three columns on one scale — the hour gutter, the event lanes, and the
 * coverage track — and it names exactly two numbers for them: `LANES_LEFT_REM`,
 * where the day begins, and `TRACK_REM`, which is how much the track takes and
 * therefore where the day *ends*. Both are read by everything that spans the
 * lane area: the block list, the markers, the hour rules and the new-item
 * draft. A third inset hard-coded anywhere in that list is how the ruler comes
 * to disagree with the lanes about where the day is — hour rules running under
 * the track, a marker's pill starting inside it.
 *
 * `CoverageTrack.tsx` draws the third column; everything else about a vendor's
 * hours is there rather than here.
 */

/**
 * The hour gutter's width — enough for `11 PM` with the rules clear of it —
 * the gap between it and the day, and so where the lane area starts when
 * nothing sits beside it.
 *
 * Numbers rather than a Tailwind class, and the reason is the day's two edges.
 * These used to be written out as the fixed class `left-[3.75rem]` on the block
 * list, the markers and the draft; the coverage rail that preceded the track
 * then sat *between* the gutter and the lanes and pushed that edge right by its
 * own content-dependent width, which is why it became a computed number.
 *
 * The track is to the right of the lanes instead, so this side is a constant
 * again — but the computed-and-shared discipline is worth keeping, because the
 * list of things that have to agree about the day's edges has not changed and
 * the other edge is now `TRACK_REM`. Every one of them reads these two numbers.
 * A hard-coded inset anywhere in that list is how a hair's breadth of
 * disagreement gets in: an hour rule running on under the track, a marker's
 * pill starting inside it, a new-item draft overhanging a column it is not part
 * of. The hour rules are in the list too — they span the lane area and must
 * stop where it stops.
 */
const GUTTER_REM = 3.25;
const GUTTER_GAP_REM = 0.5;
const LANES_LEFT_REM = GUTTER_REM + GUTTER_GAP_REM;

/** The gutter's own box, so the three places that draw one cannot disagree. */
const gutterStyle = { width: `${GUTTER_REM}rem` };

/**
 * A second hour gutter, to the right of the coverage track.
 *
 * The same width as the left one, so the two read as a pair of edges rather
 * than one edge and an afterthought. It exists because the ruler now runs
 * through the track: a capsule is read against the clock, and with labels only
 * on the far left a planner had to track back across the whole day to find out
 * what hour a window started at. A calendar with a column on each side of its
 * grid does this for the same reason.
 */
const RIGHT_GUTTER_REM = GUTTER_REM;
const rightGutterStyle = { width: `${RIGHT_GUTTER_REM}rem` };

/**
 * A coverage window as the axis is handed it: the stored row with the server's
 * wall-clock reading already on it.
 *
 * Resolved, not stored, and that is the whole contract. `hours` —
 * `'11:00 AM – 8:00 PM'` — is formatted by `resolveCoverage` from the
 * wedding's own start time, which is the one fact the axis does not have: it
 * is given offsets and a start time only so that `layout` can place pixels,
 * and a label that formatted its own times from those would be a second
 * implementation of the thing `{{coverage_hours}}` merges into an email.
 * Those two disagreeing by a minute is a vendor turning up at the wrong time,
 * so the axis prints the server's string and formats nothing.
 */
type AxisCoverage = ResolvedCoverageAnnotation<CoverageAnnotation>;

/**
 * How tall the axis gets before it scrolls itself.
 *
 * A 12½-hour day is about 1500px and the card it sits in is one tab of a page
 * that already scrolls. Left to grow, the axis would push the panel's own
 * footer a screen and a half below the fold and take the page's scrollbar with
 * it. The axis owns a scroller instead — which is also the element Task 3's
 * auto-scroll has to drive while a block is held near an edge, and the window
 * is the wrong element for that. A day short enough to fit never scrolls.
 */
const MAX_AXIS = 'max-h-[min(70vh,48rem)]';

/**
 * How long something new is before anybody has said.
 *
 * Half an hour, which is the unit the blocks are already built around — both
 * of `TimelineBlock`'s height cutoffs are set by it, because it is the
 * commonest thing on a wedding day. It is also 60px, which is a card with room
 * for its own label row clear of both resize strips.
 *
 * A click used to post `durationMinutes: null` and leave the planner to
 * discover the length afterwards, on a block that ran to whatever came next.
 * A default they can see on the draft and change before it exists is the
 * better trade, and the *point* of a default is that it is visible: the draft
 * states this length in words, and its bottom edge is there to be pulled.
 * Open-ended is still reachable, by emptying the length fields — this default
 * only changes what a planner undoes to ask for it outright, not whether they
 * can.
 *
 * The coverage track's own answer to the same question is
 * `DEFAULT_WINDOW_MINUTES`, and the two are deliberately not one constant: an
 * hour is what a vendor books and half an hour is what a day is made of.
 *
 * Exported because the axis is no longer the only place a timeline item gets
 * created with nothing said about its length. The panel's own add form and
 * the template editor's add-a-row form answer the identical question — how
 * long is a new item before anyone has said — for the identical kind of row,
 * so a second `30` written out at either call site would not be a distinct
 * design decision re-arrived-at, it would be this one copied and liable to
 * drift from it. That is not the same case as `DEFAULT_WINDOW_MINUTES` above:
 * a coverage window and a timeline item are different things that happen to
 * each want a default, and nothing says their defaults must move together.
 */
export const DRAFT_DURATION_MINUTES = 30;

/**
 * The shortest a draft's card is drawn, whatever it has been dragged to.
 *
 * Two eight-pixel resize strips with the label row between them. The same
 * trade `MIN_CARD_PX` makes on a committed block, for the same reason: below
 * this the drawn box rounds up while `top` stays exact, because a draft
 * trimmed to five minutes with no edge left to grab has no way back to a
 * longer length.
 */
const DRAFT_CARD_PX = 56;

/**
 * The subject id a draft's drag carries.
 *
 * The hook keys a gesture by id because the axis has to find the row to PATCH
 * when one lands. A draft has no row — its drag lands in the draft — so this
 * is a name, not a lookup.
 */
const DRAFT_ID = 'draft';

/** And the track's, which lands in `coverageDraft` rather than in a PATCH. */
const COVERAGE_DRAFT_ID = 'coverage-draft';

/** Where the planner clicked, and how long they have since made it. */
interface Draft {
  offsetMinutes: number;
  /**
   * Always a number, unlike a stored item's. Null on a row means "run to the
   * end of the night", which is a thing only an item with neighbours can mean;
   * a draft has a length from the moment it appears.
   */
  durationMinutes: number;
}

/**
 * The scroller's window, in client coordinates.
 *
 * The only thing a drag needs that a difference of two `clientY` values cannot
 * give it, and therefore the only thing measured — once, when the pointer goes
 * down. Injectable because a DOM with no layout engine answers zero to every
 * box, and auto-scroll is not testable against a window it is told is zero
 * pixels tall.
 */
const measureViewport = (scroller: HTMLElement): ScrollWindow => ({
  top: scroller.getBoundingClientRect().top,
  height: scroller.clientHeight,
});

export default function TimelineAxis({
  items,
  startTime,
  endTime,
  weddingDate,
  coverage = [],
  categories = [],
  onEditField,
  onCreate,
  onDelete,
  onRetime,
  onPlaceCoverage,
  onRetagCoverage,
  onRetimeCoverage,
  onRemoveCoverage,
  deletingId = null,
  viewport = measureViewport,
  anchorKey,
}: {
  items: readonly TimelineItemView[];
  startTime: string | null;
  endTime: string | null;
  /**
   * The wedding's own calendar date, which the date selector above the axis is
   * named from. Null is ordinary — a planner can have hours before they have a
   * date — and the selector falls back to naming days relative to the
   * wedding's.
   */
  weddingDate: string | null;
  /**
   * The vendors' booked hours, drawn as capsules in the track beside the day.
   *
   * Optional and defaulted to empty, like `coverage` on `layout` itself: every
   * drag and edit test mounts the axis without it. With no windows and no
   * placement handler there are no capsules and no gesture — but the track
   * itself is drawn and the day still stops at `TRACK_REM`, which is deliberate
   * and is the change the review asked for twice over: a track the day gives no
   * room to is a track nobody can find.
   *
   * The axis fetches no wedding data: the panel owns the query and hands the
   * resolved rows down, which is the same arrangement `items` has and for the
   * same reason — one place knows a wedding's id and one knows how to draw a
   * day. (The tag control inside the track does read the planner's own category
   * list, which belongs to no wedding; see `CoverageTrack`.)
   */
  coverage?: readonly AxisCoverage[];
  /**
   * The planner's own category names, which a window's tag is picked from.
   *
   * Handed down like everything else and defaulted to empty: the axis fetches
   * nothing, which is what lets it be rendered in a bare DOM with no query
   * client. An empty list is a working state, not a broken one — the tag
   * control still offers "Something else…", so a planner with no categories
   * yet can name a window in free text.
   */
  categories?: readonly string[];
  /**
   * One committed field, one PATCH. There is no form and no Save.
   *
   * In both grammars, and the pairing is not arbitrary: a label or a note is a
   * field as the planner typed it, while a *time* arrives here as a clock time
   * and leaves as `offsetMinutes` — the axis knows which day the item is on, so
   * it resolves the time itself rather than handing a bare `2:00` on for
   * something further down to guess the day of. See `editField`.
   */
  onEditField: (item: TimelineItemView, patch: Partial<TimelineItemDraft> & TimelineRetime) => void;
  /** A click on empty axis space, once it has been given a name. An offset,
   *  not a clock time — see `TimelineItemPlacement`. */
  onCreate: (placement: TimelineItemPlacement) => void;
  onDelete: (item: TimelineItemView) => void;
  /** One PATCH per gesture: a drag that lands, or an arrow key that nudges. */
  onRetime: (item: TimelineItemView, patch: TimelineRetime) => void;
  /**
   * A span swept out of the empty track and then given a tag. One POST.
   *
   * The four coverage handlers are intents reported upward and nothing else:
   * the axis runs no mutation and holds no server state for a window, exactly
   * as it does not for an item. All four are optional, and the first of them is
   * the switch — with no `onPlaceCoverage` the track's ground is not a target
   * and does not invite one, so an axis mounted without them draws the column
   * and nothing that promises a gesture.
   *
   * A window is now edited the way a block is: in place, one gesture at a time.
   * Its times are the capsule, dragged; its tag is the same capsule, clicked.
   * There is no form, and that is the point of the redesign — a form was a
   * second grammar for saying when, on a timeline where everything else is
   * placed by pointing.
   */
  onPlaceCoverage?: (placement: CoveragePlacement) => void;
  /**
   * The tag swapped on a window that already exists.
   *
   * Both tags, always, one of them null — `coverageTagsNamedTogether` refuses a
   * request that names one alone, because whether `{ category: null }` leaves a
   * row untagged depends on what is already stored.
   */
  onRetagCoverage?: (annotation: AxisCoverage, tags: CoverageTags) => void;
  /**
   * A capsule dropped somewhere new, or re-lengthed by an edge, or nudged with
   * an arrow key. One PATCH per gesture, in the offset grammar.
   *
   * And carrying **no tag**, which is not an omission: sending one tag alone is
   * a 400, and sending both would be a retag the planner never asked for.
   */
  onRetimeCoverage?: (annotation: AxisCoverage, patch: CoverageRetime) => void;
  onRemoveCoverage?: (annotation: AxisCoverage) => void;
  deletingId?: string | null;
  viewport?: (scroller: HTMLElement) => ScrollWindow;
  /**
   * Which day this is — the wedding's id.
   *
   * The axis opens scrolled to event start, and this is what "opens" means:
   * the view is placed once per day shown, not once per render. Changing it
   * places the view again, which is what switching weddings has to do.
   */
  anchorKey?: string;
}) {
  /**
   * What the planner has asked for but not yet named.
   *
   * A draft rather than a row: the schema wants a label and a planner has not
   * typed one yet, so a click that posted immediately would either be refused
   * or would put an item called nothing on the day. It lives here until it has
   * a name, and then it is one POST.
   *
   * It carries a length as well as a minute, because the draft is resizable
   * before it is real — which is the one edit a planner cannot make any other
   * way. Every other field on an item is reachable once the item exists; the
   * length of a thing that does not exist yet is only reachable here.
   */
  const [draft, setDraft] = useState<Draft | null>(null);
  /**
   * Which click this draft is.
   *
   * The draft's field is keyed on this and not on its offset. Keyed on the
   * offset — which it was, while a draft could not move — a body or top-edge
   * drag remounts the input and takes the half-typed label with it, which is
   * every drag this feature adds.
   */
  const [draftKey, setDraftKey] = useState(0);

  /**
   * Which calendar day is on the canvas, counted from the wedding's own date.
   *
   * View state and nothing else: no column stores it and no request carries it,
   * because a timeline item is a signed offset from `weddings.startTime` and
   * "which day am I looking at" is a window over that. It lives here with the
   * drafts rather than in the panel because the panel owns the queries and
   * knows nothing about pixels — and because a drag that carries a block past
   * midnight has to move this from inside a pointer gesture.
   */
  const [dayIndex, setDayIndex] = useState(0);
  /**
   * The same number, reachable from inside a gesture.
   *
   * A pointermove that crosses a day boundary before React has re-rendered has
   * to see its own first answer, or one crossing would shift the view twice.
   * The same discipline `hostRef` and `driveRef` keep in `use-timeline-drag.ts`,
   * and for the same reason.
   */
  const liveDay = useRef(dayIndex);
  liveDay.current = dayIndex;

  const axis: TimelineLayout<TimelineItemView, AxisCoverage> = layout(
    items,
    startTime,
    endTime,
    coverage,
    dayIndex,
  );

  const scrollerRef = useRef<HTMLDivElement | null>(null);


  /**
   * Carry the view onto the day a gesture has reached.
   *
   * One day at a time means a block dragged past midnight has nowhere to be on
   * this canvas, so the canvas changes under it. The block lands near the top
   * of the new day rather than staying under the pointer, and that is inherent
   * rather than a compromise: there is no canvas above midnight for it to sit
   * in. The pointer keeps differential control from there, and with it still
   * near the scroller's edge the auto-scroll carries on pulling, so the gesture
   * continues across the boundary instead of ending at it.
   *
   * Returns the pixels the scroller actually moved, which is what the gesture
   * corrects its own frame by — see `follow` on `TimelineDragHost`. Clamped
   * here rather than left to the browser: the requested shift is a whole day
   * and the scroller can show at most 70vh of one, so it is nearly always out
   * of range, and a correction made from the request rather than the result
   * would reintroduce exactly the error it exists to remove.
   */
  const follow = (offsetMinutes: number) => {
    const next = clampDayIndex(startTime, dayIndexForOffset(startTime, offsetMinutes));
    if (next === liveDay.current) return 0;
    const moved = (next - liveDay.current) * DAY_MINUTES * PX_PER_MINUTE;
    liveDay.current = next;
    setDayIndex(next);
    const scroller = scrollerRef.current;
    if (!scroller) return 0;
    const before = scroller.scrollTop;
    // The canvas moved up by `moved` pixels, so the same offsets stay in view
    // if the scroller moves down by the same amount — as far as the day can.
    const limit = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTop = Math.min(limit, Math.max(0, before - moved));
    return scroller.scrollTop - before;
  };

  const byId = new Map(items.map((item) => [item.id, item]));

  /**
   * One committed field, with a typed clock time turned into the offset the
   * rows are stored in.
   *
   * `offsetForClockTime` cannot do this and is no longer asked to. A bare
   * `2:00` names four offsets inside the stored bound and nothing in the string
   * says which, so it settles the question with a documented twelve-hour
   * cutoff: against a 17:30 wedding, 05:30 is the tail of the night and 06:00
   * is the morning before it. That guess is the right rule for a form with no
   * day in it, and the wrong one for a block the planner is looking at — which
   * is on a day, and says which. Retyping a 2 AM item's time used to resolve
   * against the guess and land a full calendar day away; the only thing between
   * the planner and that was `commitTime` declining to send an unchanged value.
   *
   * Resolved against **the item's own day**, not the day in view. The two
   * differ only for a block that crosses midnight and is being read from the
   * far side of it, and there the planner is correcting a time on a block they
   * can see rather than re-dating it. It also keeps the conversion a pure
   * function of the item, with no view state in it at all.
   *
   * Bounded through the same `clampOffsetMinutes` a drag and an arrow key go
   * through, and for a reason the typed path did not have before: it used to
   * send a clock time, which the server resolved into range by construction,
   * and it now sends an offset that a planner typing on a far-out day could put
   * past what the schema stores.
   */
  const editField = (item: TimelineItemView, patch: Partial<TimelineItemDraft>) => {
    const { clockTime, ...rest } = patch;
    if (clockTime === undefined) {
      onEditField(item, rest);
      return;
    }
    const resolved = offsetForClockTimeOnDay(
      startTime,
      dayIndexForOffset(startTime, item.offsetMinutes),
      clockTime,
    );
    // No start time, no offsets: there is nothing for a clock time to be
    // measured from, and the field is not reachable on such a wedding anyway.
    if (resolved === null) return;
    const offsetMinutes = clampOffsetMinutes(resolved);
    // An unchanged field is not a request; the API rejects an empty PATCH.
    if (offsetMinutes === item.offsetMinutes) {
      if (Object.keys(rest).length > 0) onEditField(item, rest);
      return;
    }
    onEditField(item, { ...rest, offsetMinutes });
  };

  const retime = (item: TimelineItemView, patch: TimelineRetime) => {
    // Not a second clamp. A drag arrives already bounded by the hook, but the
    // keyboard does not: `nudgedOffset` is unbounded arithmetic on the item's
    // own offset, so for an arrow key this is the only thing between the
    // planner and a 400. The two combine into the behaviour an arrow key at the
    // limit should have — the offset clamps back onto the one the item already
    // has, and the check below turns that into no request at all rather than a
    // refused one.
    const bounded =
      patch.offsetMinutes === undefined
        ? patch
        : { ...patch, offsetMinutes: clampOffsetMinutes(patch.offsetMinutes) };
    // An unchanged field is not a request; the API rejects an empty PATCH.
    if (bounded.offsetMinutes === item.offsetMinutes) delete bounded.offsetMinutes;
    if (Object.keys(bounded).length === 0) return;
    onRetime(item, bounded);
  };

  const { drag, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, claimClick } =
    useTimelineDrag({
      scale: axis,
      totalHeight: axis.totalHeight,
      viewport,
      follow,
      commit: (id, patch) => {
        const item = byId.get(id);
        if (item) retime(item, patch);
      },
    });

  const grab = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };

  /**
   * And a third instance, for the thing that is not a row yet.
   *
   * The same reasoning as the track's above, arriving somewhere else: what
   * separates these gestures is where they land. An item's ends in a PATCH, a
   * window's in a PATCH to another table, and a draft's in the draft — nothing
   * reaches the server until the planner has named it. Everything before the
   * landing is one question about one axis: the four-pixel threshold that tells
   * a click from a drag, the capture that survives leaving an eight-pixel
   * strip, the auto-scroll that pulls the day at the scroller's edge, the map
   * that makes a pixel a minute, and `edgeDrag`'s refusal to produce a negative
   * length. A draft is owed all of it and none of it is worth writing twice.
   */
  const {
    drag: draftDrag,
    claimClick: claimDraftClick,
    ...draftGrab
  } = useTimelineDrag({
    scale: axis,
    totalHeight: axis.totalHeight,
    viewport,
    commit: (_id, patch) =>
      setDraft((current) => {
        if (current === null) return current;
        return {
          offsetMinutes: patch.offsetMinutes ?? current.offsetMinutes,
          // The null the hook's type allows is not a case here: a draft's
          // length is always a number, so there is never an open end to
          // preserve and the hook never sends one. Same impossibility the
          // track narrows away above, and for the same reason.
          durationMinutes: patch.durationMinutes ?? current.durationMinutes,
        };
      }),
  });

  /**
   * A second instance of the same hook for the rail, not a second
   * implementation of a drag.
   *
   * Two instances rather than one, because `commit` is keyed by the subject's
   * id and an item id and a window id come out of two different tables: one
   * hook would have to be told which table each gesture was about, and a
   * `byId.get` that quietly missed would post a block's new offset to a
   * coverage row. They are also genuinely two gestures — only one of them can
   * be running, but each has its own live state and its own commit — so sharing
   * the instance would buy nothing but the ambiguity.
   *
   * A window's `durationMinutes` is never null (see `TimelineCoverageSpan`), so
   * the `effectiveDurationMinutes` the hook wants is the stored field itself and
   * the open-ended case the blocks have simply does not arise.
   */
  const coverageById = new Map(coverage.map((annotation) => [annotation.id, annotation]));

  /**
   * One way a window's times reach the server, whatever moved them.
   *
   * Two things do now — a drag of a capsule's edge, and a time typed into its
   * panel — and the bounding below is the same question for both: is this
   * inside what the schema stores, is it long enough to be a window, and is
   * there anything here to send at all. A second copy of that for the typed
   * path is a second place for one of the three to be forgotten.
   *
   * For the typed path the clamp is not belt-and-braces, it is the whole of it:
   * a drag arrives already bounded by the hook, but nothing bounds a planner
   * typing `3:00` against a wedding whose day does not reach it.
   */
  const retimeCoverage = (annotation: TrackCoverage, patch: CoverageRetime) => {
    const retime: CoverageRetime = {};
    if (patch.offsetMinutes !== undefined) {
      const offsetMinutes = clampOffsetMinutes(patch.offsetMinutes);
      if (offsetMinutes !== annotation.offsetMinutes) retime.offsetMinutes = offsetMinutes;
    }
    // Shorter than the floor is not a window. The route refuses it either way;
    // refusing it here is what keeps a mistyped end from reading to the planner
    // as a save that failed for no stated reason.
    if (
      patch.durationMinutes !== undefined &&
      patch.durationMinutes >= MIN_WINDOW_MINUTES &&
      patch.durationMinutes !== annotation.durationMinutes
    ) {
      retime.durationMinutes = patch.durationMinutes;
    }
    // An empty PATCH is not a request; the API rejects one by design.
    if (Object.keys(retime).length === 0) return;
    // No tag in the payload, and that is load-bearing: one tag alone is a 400,
    // and both would be a retag the planner did not ask for.
    onRetimeCoverage?.(annotation, retime);
  };

  const coverageDrag = useTimelineDrag({
    scale: axis,
    totalHeight: axis.totalHeight,
    viewport,
    // A capsule crosses midnight as readily as a block does — a photographer's
    // nine hours routinely close after it — so the view follows one of these
    // too. The two drafts below are deliberately left without it: paging the
    // day away from a span nobody has tagged yet would take the thing the
    // planner is naming off the screen.
    follow,
    commit: (id, patch) => {
      const annotation = coverageById.get(id);
      if (!annotation) return;
      // Narrowed rather than passed through, and the one difference is a null.
      // `TimelineRetime.durationMinutes` is nullable because a *block* can
      // store null to mean "runs to the end of the night"; a window always
      // states its own end and the route refuses a null length. The hook cannot
      // produce one here either — that branch needs
      // `subject.durationMinutes === null` — so this drops an impossible value
      // instead of posting it, and `CoverageRetime` is what makes the
      // impossibility say so in the type.
      retimeCoverage(annotation, {
        ...(patch.offsetMinutes !== undefined ? { offsetMinutes: patch.offsetMinutes } : {}),
        ...(typeof patch.durationMinutes === 'number'
          ? { durationMinutes: patch.durationMinutes }
          : {}),
      });
    },
  });

  /**
   * The span a sweep produced, waiting for its tag.
   *
   * A draft rather than a row, and the same reasoning as `draft` above: a
   * window has to carry exactly one tag — `coverageHasExactlyOneTag` — and a
   * planner who has just let go of a sweep has named neither. It lives here
   * until it has one, and then it is one POST.
   */
  const [coverageDraft, setCoverageDraft] = useState<CoverageSpan | null>(null);

  /**
   * And a fourth instance, for the window that is not a row yet.
   *
   * Four, which is one per place a gesture can land rather than four
   * implementations of a gesture: a block's PATCH, a window's PATCH, and the
   * two drafts, each of which lands in its own state and reaches the server
   * only once it has been named or tagged. The hook keys by subject id and the
   * ids come from different tables, so one instance would have to be told which
   * table each gesture was about — and a lookup that quietly missed would post
   * a window's new length to a timeline row.
   *
   * A window's length is never null, so the open-ended case the blocks have
   * does not arise here any more than it does for the track's own capsules.
   */
  const {
    drag: coverageDraftDrag,
    claimClick: claimCoverageDraftClick,
    ...coverageDraftGrab
  } = useTimelineDrag({
    scale: axis,
    totalHeight: axis.totalHeight,
    viewport,
    commit: (_id, patch) =>
      setCoverageDraft((current) => {
        if (current === null) return current;
        return {
          offsetMinutes: patch.offsetMinutes ?? current.offsetMinutes,
          durationMinutes: patch.durationMinutes ?? current.durationMinutes,
        };
      }),
  });
  /** Which existing window's tag editor is open. Its times are not in it. */
  const [retagging, setRetagging] = useState<string | null>(null);


  const sweep = useCoverageSweep({
    scale: axis,
    totalHeight: axis.totalHeight,
    viewport,
    minMinutes: MIN_WINDOW_MINUTES,
    // Two numbers, and the pair is the point: the floor is what a *swept* span
    // has to clear to be used as a length, and this is what a press that swept
    // nothing makes instead. See `DEFAULT_WINDOW_MINUTES`.
    defaultMinutes: DEFAULT_WINDOW_MINUTES,
    onSweep: (span) => {
      setRetagging(null);
      setCoverageDraft(span);
    },
  });

  /**
   * The day's two edges, worked out once and read by everything that spans it.
   *
   * `lanesLeft` is where the lane area begins and `dayRight` is how far short of
   * the card's right edge it stops — the width the coverage track takes, which
   * is `TRACK_REM` and nothing derived. Both are handed to the block list, the
   * markers, the hour rules, the new-item draft and the click-catcher behind
   * them all.
   *
   * `dayRight` rather than the old `right-0`, and it is the whole of what makes
   * the track a third column rather than an overlay on the second: without it
   * every rule the axis draws runs on *under* the track's ground, and a planner
   * reading a capsule against the hour beside it is reading a ruler drawn
   * behind a wall.
   */
  const lanesLeft = `${LANES_LEFT_REM}rem`;
  /** Where the lane area stops: the track, plus the gutter beyond it. */
  const dayRight = `${TRACK_REM + RIGHT_GUTTER_REM}rem`;
  /** Where the track sits, held off the edge by that gutter. */
  const trackRight = `${RIGHT_GUTTER_REM}rem`;
  /** The gutter's gap, as the margin that starts the rules after it. */
  const laneRuleMargin = `${GUTTER_GAP_REM}rem`;

  const hours = hourTicks(axis, startTime);

  /**
   * Open on event start.
   *
   * A layout effect rather than an effect: the scroller's own default is the
   * top of the canvas — midnight, now that the axis is a whole calendar date —
   * and in `useEffect` that default is painted once before the anchor replaces
   * it, which the planner sees as the day jumping. The further the default is
   * from where the view belongs, the worse that jump reads, so the calendar-day
   * axis makes the choice of a layout effect matter more than it did.
   *
   * Keyed on the day, and deliberately *not* on `axis`, which is rebuilt on
   * every render. The panel refetches the whole timeline after each inline
   * edit and each drag that lands, so an anchor that watched the layout would
   * re-fire on all of them and throw a planner working on the evening back to
   * the ceremony every time they typed. Placing the view is something that
   * happens when a day is opened; after that it is the planner's to scroll.
   */
  useLayoutEffect(() => {
    // No day named, no view placed. An axis rendered without a key is one
    // nobody has said is a particular wedding's, and moving its scroller would
    // be the component deciding something it was not told.
    if (anchorKey === undefined) return;
    if (scrollerRef.current) scrollerRef.current.scrollTop = anchorScrollTop(axis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey]);

  /**
   * Which minute a pointer at this `clientY` is over.
   *
   * Through `offsetForTop`, the same piecewise map the blocks and the gutter
   * were drawn with — every pixel below a collapsed stretch is hours further
   * down the day than a ruler would say, and a linear reading would put a new
   * item nowhere near where the planner pointed.
   *
   * Clamped as well as snapped, and the clamp is not cosmetic: the axis can be
   * scrolled past the furthest offset the schema stores, so an unbounded read
   * is an offset the route refuses. It bounds the posted value as well as the
   * preview, because a click posts this offset rather than the clock time it
   * reads out. While it posted a clock time the clamp could not have done that,
   * and could not have been fixed into doing it either: `clockTimeForOffset`
   * wraps mod 1440, so an *unbounded* draft at -3900 against a 17:30 start
   * would have posted "00:30" for the server to read back as +420 — and the
   * clamp that was already here did not help, because -3900 clamped to -2880,
   * posted "17:30", and landed at offset 0. Sending the offset is what closed
   * that wrap; the clamp is what keeps the offset inside `MAX_OFFSET_MINUTES`,
   * so the schema takes what the planner pointed at.
   *
   * One function rather than the arithmetic inline, so that every gesture
   * reading a pixel as an hour reads it the same way.
   */
  const offsetAtPointer = (clientY: number) => {
    const scroller = scrollerRef.current;
    const window = scroller ? viewport(scroller) : { top: 0, height: 0 };
    const canvasY = clientY - window.top + (scroller ? scroller.scrollTop : 0);
    return clampOffsetMinutes(snapMinutes(offsetForTop(axis, canvasY)));
  };

  /** A click on empty axis space is a time, and the time is what it means. */
  const startDraft = (event: ReactMouseEvent<HTMLElement>) => {
    // The click a finished drag leaves behind lands wherever the pointer came
    // to rest, which is very often empty space. Both drags are asked, and both
    // are asked *before* the `||` rather than through it: `claimClick` clears
    // the flag it reports, and short-circuiting would leave the other hook's
    // flag standing to swallow the next genuine click on the day.
    const afterItemDrag = claimClick();
    const afterCoverageDrag = coverageDrag.claimClick();
    const afterDraftDrag = claimDraftClick();
    const afterCoverageDraftDrag = claimCoverageDraftClick();
    if (afterItemDrag || afterCoverageDrag || afterDraftDrag || afterCoverageDraftDrag) return;
    setDraft({
      offsetMinutes: offsetAtPointer(event.clientY),
      durationMinutes: DRAFT_DURATION_MINUTES,
    });
    setDraftKey((previous) => previous + 1);
  };

  /** The same arrangement for the track's draft. See `drawnDraft` below. */
  const drawnCoverageDraft =
    coverageDraft &&
    coverageDraftDrag && {
      offsetMinutes: coverageDraftDrag.offsetMinutes,
      durationMinutes: coverageDraftDrag.durationMinutes,
    };

  /**
   * Where the draft is drawn, which while it is being dragged is not where it
   * is stored.
   *
   * The same arrangement the blocks and the capsules have, and for the same
   * reason: the stored value is what a drop will commit and the live one is
   * what the planner is looking at. Keeping them apart is what lets a gesture
   * be abandoned.
   */
  const drawnDraft = draft && {
    offsetMinutes: draftDrag ? draftDrag.offsetMinutes : draft.offsetMinutes,
    durationMinutes: draftDrag ? draftDrag.durationMinutes : draft.durationMinutes,
  };

  return (
    <div>
      {/* The one row above the scroller, and the only thing on screen that says
          which day this is. The rail used to want a row here for the word
          "Coverage" and did not get one — a caption inside the scroller would
          have scrolled away from the column it named, and the track carries its
          own sticky heading instead. This is a different claim: it names the
          canvas rather than a column in it, and it has to stay put while the
          day scrolls under it.

          Paging keeps the planner's place in the day by doing nothing to the
          scroller: every day is the same height, so `scrollTop` still means the
          same hour on the day it moves to. */}
      <TimelineDateSelector
        dayIndex={dayIndex}
        weddingDate={weddingDate}
        startTime={startTime}
        onChange={setDayIndex}
      />
      <div
        ref={scrollerRef}
        data-timeline-scroll
        className={`relative ${MAX_AXIS} overflow-y-auto overscroll-contain pr-1`}
      >
        <div data-timeline-canvas className="relative" style={{ height: axis.totalHeight }}>
          {/* Behind everything, so it only ever hears the clicks nothing else
              wanted. The hour rules are inert and the blocks sit above it, which
              leaves exactly the empty day to answer for.

              Bounded at the track like the rest of the day. It would behave
              either way — the track is this element's *sibling*, so a click on
              the track's ground has never bubbled through here — but what this
              box is for is the empty day, and the track is not the day. Leaving
              it spanning both columns would be one more place a later reader
              has to work that out. */}
          <div
            data-timeline-new
            aria-hidden="true"
            className="absolute inset-y-0 left-0"
            style={{ right: dayRight }}
            onClick={startDraft}
          />

          {/* The track's ground, before the rules so they run across it — see
              `CoverageGround`. The track itself comes further down and paints
              nothing; this is the one piece of it that has to be under the
              ruler rather than over it. */}
          <CoverageGround rightRem={RIGHT_GUTTER_REM} />

          {hours.map((hour) => (
            <div
              key={hour.offsetMinutes}
              data-hour-offset={hour.offsetMinutes}
              /* Across the lane area *and* the track, stopping at the right
                 gutter. It used to stop at the track on the reasoning that an
                 hour of the wedding's day had no business being drawn through a
                 column that is not the day — but a capsule is placed by the
                 clock and read against it, and a ruler that stops at the column
                 makes the planner track back across the whole day to tell what
                 hour a window opens at. The hours are the one thing the two
                 columns genuinely share; it is the same scale down both. */
              /* The whole canvas, `right-0`, and the right-hand label is the
                 gutter at the end of it. Stopping this row at the ruler's edge
                 instead put that label *inside* the row's last `RIGHT_GUTTER_REM`
                 — which is the track's last 3.25rem — and printed the hour on
                 top of the capsules. The rule is what stops at the track's edge,
                 not the row. */
              className="pointer-events-none absolute inset-x-0 flex -translate-y-1/2 items-center"
              style={{ top: hour.top }}
            >
              {/* On the white card, `text-muted-foreground` measures 4.55:1 — the
                  one surface in this panel where it clears the floor. */}
              <span
                data-hour-label="left"
                className="label-caps shrink-0 text-right text-[0.625rem] tabular-nums text-muted-foreground"
                style={gutterStyle}
              >
                {hour.label}
              </span>
              {/* A margin rather than a `gap-2`, which is how this row was
                  changed when the rail sat between the gutter and the lanes: a
                  `gap` applies either side of anything inserted between two
                  children, so a spacer in the flow put the rule half a rem
                  further right than the lanes it is the ruler for. The spacer is
                  gone with the rail, but the margin stays — it is the one form
                  of this that says the rule starts on exactly `LANES_LEFT_REM`,
                  which is the pixel the blocks beside it start on. */}
              <span className="h-px flex-1 bg-border" style={{ marginLeft: laneRuleMargin }} />
              {/* The same hour again, on the far side of the track. Right-aligned
                  into its own gutter so the two edges mirror each other, and
                  `tabular-nums` like its twin so the two columns of times line
                  up down the day rather than wandering by a digit. */}
              <span
                data-hour-label="right"
                className="label-caps shrink-0 pl-2 text-left text-[0.625rem] tabular-nums text-muted-foreground"
                style={rightGutterStyle}
              >
                {hour.label}
              </span>
            </div>
          ))}

          {/* `role="list"` because `list-style: none` drops the list semantics in
              Safari's screen reader, and the count of things in the day is worth
              hearing. */}
          {/* `pointer-events-none` on the list and back on for each block: the
              list's own box spans the whole lane area, and left live it would
              swallow every click on the empty day behind it. */}
          {/* Both edges from the two shared numbers, so the lanes a block
              divides are exactly the lanes the hour rules above it measure. The
              right edge is what keeps a nine-hour block from running under the
              coverage track — which it would read as part of, the two being
              solid shapes meeting edge to edge. */}
          <ol
            role="list"
            className="pointer-events-none absolute inset-y-0 m-0 list-none p-0"
            style={{ left: lanesLeft, right: dayRight }}
          >
            {carried(axis, drag, byId).map(({ item, placement, effectiveDurationMinutes }) => {
              const dragging = drag?.id === item.id;
              return (
                <TimelineBlock
                  key={item.id}
                  item={item}
                  placement={dragging ? dragPlacement(axis, drag, placement) : placement}
                  deleting={deletingId === item.id}
                  dragging={dragging}
                  readout={dragging ? readoutFor(drag, startTime) : null}
                  grab={{
                    ...grab,
                    onPointerDown: (kind, event) =>
                      onPointerDown(
                        {
                          id: item.id,
                          offsetMinutes: item.offsetMinutes,
                          durationMinutes: item.durationMinutes,
                          effectiveDurationMinutes,
                        },
                        kind,
                        event,
                      ),
                  }}
                  // The click a finished drag leaves behind is the drag's own
                  // shadow. `claimClick` is what keeps it from opening a field
                  // over the top of the change it just made.
                  claimClick={claimClick}
                  onCommit={(patch) => editField(item, patch)}
                  onNudge={(steps, fine) =>
                    retime(item, {
                      offsetMinutes: nudgedOffset(item.offsetMinutes, steps, fine),
                    })
                  }
                  onDelete={onDelete}
                />
              );
            })}
          </ol>

          {draft !== null && drawnDraft !== null && (
            <NewItemDraft
              key={draftKey}
              top={topForOffset(axis, drawnDraft.offsetMinutes)}
              height={draftHeight(axis, drawnDraft)}
              left={lanesLeft}
              right={dayRight}
              clock={clockAt(startTime, drawnDraft.offsetMinutes)}
              durationMinutes={drawnDraft.durationMinutes}
              readout={draftDrag ? readoutFor(draftDrag, startTime) : null}
              grab={{
                ...draftGrab,
                // The *stored* draft, not the drawn one: a gesture measures
                // from where the thing was when it was grabbed, and handing it
                // the live position would compound each move onto the last.
                onPointerDown: (kind, event) =>
                  draftGrab.onPointerDown(
                    {
                      id: DRAFT_ID,
                      offsetMinutes: draft.offsetMinutes,
                      durationMinutes: draft.durationMinutes,
                      effectiveDurationMinutes: draft.durationMinutes,
                    },
                    kind,
                    event,
                  ),
              }}
              onCancel={() => setDraft(null)}
              onCreate={(label) => {
                setDraft(null);
                onCreate({
                  offsetMinutes: draft.offsetMinutes,
                  label,
                  durationMinutes: draft.durationMinutes,
                  note: null,
                });
              }}
            />
          )}

          {/* Drawn last so they read over the blocks they cut across. An item
              that runs past the end stays where it is in time — below the line,
              and flagged. Dropping it would hide the one collision the planner
              needs to see. */}
          {/* On the day it falls on, which is the wedding's own: the axis is one
              date now, so the hours the couple booked are not on the morning
              before or the morning after and a line there would be a fact
              drawn on a day it is not about. The end-of-night line answers the
              same question through `endOfNightTop`, which is null off its own
              day. */}
          {startTime && axis.startOffsetMinutes <= 0 && 0 <= axis.endOffsetMinutes && (
            <Marker
              kind="start"
              top={topForOffset(axis, 0)}
              left={lanesLeft}
              trackRem={TRACK_REM}
              gutterRem={RIGHT_GUTTER_REM}
              label={`Event starts ${formatTimeOfDay(startTime) ?? startTime}`}
            />
          )}
          {axis.endOfNightTop !== null && endTime && (
            <Marker
              kind="end"
              top={axis.endOfNightTop}
              left={lanesLeft}
              trackRem={TRACK_REM}
              gutterRem={RIGHT_GUTTER_REM}
              label={`Event ends ${formatTimeOfDay(endTime) ?? endTime}`}
            />
          )}

          {/* The coverage track: the third column, and the last thing on the
              canvas.

              Layer order *across* the two areas has stopped being load-bearing,
              which is the structural half of what the extra width bought.
              Nothing in the day reaches into the track — the list, the rules,
              the dividers, the draft and the markers all stop at `dayRight` —
              and nothing in the track reaches into the day. On the rail this
              was a careful three-way argument settled by document order, and
              the comments arguing it are gone with the floating labels that
              needed it: capsules below the blocks so they read over the hour
              rules, labels *above* the blocks or they were unclickable under
              the first photograph of the day, markers above those.

              What the position still buys is order *within* the track: its
              sticky heading and its inline tag editor are over its own
              capsules, which is where both have to be.

              Rendered on every wedding, with or without windows and with or
              without a way to place one. The day stops at `TRACK_REM` either
              way, and the column is what accounts for that width — a bounded
              day beside no column is a white gap nobody can explain. */}
          <CoverageTrack
            rightRem={trackRight}
            bands={axis.coverageBands}
            sweeping={sweep.sweep && spanPlacement(axis, sweep.sweep)}
            draft={
              coverageDraft && {
                ...spanPlacement(axis, drawnCoverageDraft ?? coverageDraft),
                readout: coverageDraftDrag ? readoutFor(coverageDraftDrag, startTime) : null,
                ...(onPlaceCoverage
                  ? {
                      grab: {
                        ...coverageDraftGrab,
                        // The *stored* span, for the same reason the timeline
                        // draft hands over its stored one: a gesture measures
                        // from where the thing was when it was grabbed.
                        onPointerDown: (kind: DragKind, event: ReactPointerEvent<HTMLElement>) =>
                          coverageDraftGrab.onPointerDown(
                            {
                              id: COVERAGE_DRAFT_ID,
                              offsetMinutes: coverageDraft.offsetMinutes,
                              durationMinutes: coverageDraft.durationMinutes,
                              effectiveDurationMinutes: coverageDraft.durationMinutes,
                            },
                            kind,
                            event,
                          ),
                      },
                    }
                  : {}),
              }
            }
            onSweepPointerDown={
              onPlaceCoverage
                ? (event) => {
                    // A press on the track is not a press on the day. An open
                    // draft is abandoned rather than left behind the new one.
                    setCoverageDraft(null);
                    setRetagging(null);
                    sweep.onPointerDown(event);
                  }
                : undefined
            }
            onSweepPointerMove={sweep.onPointerMove}
            onSweepPointerUp={sweep.onPointerUp}
            onSweepPointerCancel={sweep.onPointerCancel}
            dragging={coverageDragPlacement(axis, coverageDrag.drag, startTime)}
            onCapsulePointerDown={
              onRetimeCoverage
                ? (annotation, kind, event) =>
                    coverageDrag.onPointerDown(
                      {
                        id: annotation.id,
                        offsetMinutes: annotation.offsetMinutes,
                        durationMinutes: annotation.durationMinutes,
                        effectiveDurationMinutes: annotation.durationMinutes,
                      },
                      kind,
                      event,
                    )
                : undefined
            }
            onCapsulePointerMove={coverageDrag.onPointerMove}
            onCapsulePointerUp={coverageDrag.onPointerUp}
            onCapsulePointerCancel={coverageDrag.onPointerCancel}
            onNudge={
              onRetimeCoverage
                ? (annotation, steps, fine) => {
                    // The same bounding `retime` does for an item, and for the
                    // same reason: a drag arrives already clamped by the hook,
                    // but `nudgedOffset` is unbounded arithmetic on the row's own
                    // offset, so for an arrow key this is the only thing between
                    // the planner and a 400.
                    const offsetMinutes = clampOffsetMinutes(
                      nudgedOffset(annotation.offsetMinutes, steps, fine),
                    );
                    // An unchanged field is not a request; the API rejects an
                    // empty PATCH by design.
                    if (offsetMinutes === annotation.offsetMinutes) return;
                    onRetimeCoverage(annotation, { offsetMinutes });
                  }
                : undefined
            }
            categories={categories}
            editing={retagging}
            onEdit={
              onRetagCoverage
                ? (annotation) => {
                    // The click a finished capsule drag leaves behind lands on
                    // the capsule it just moved, because the capsule is now
                    // both the drag handle and the way into the tag editor —
                    // the floating label that used to be the second control is
                    // gone. `claimClick` is what keeps that shadow from opening
                    // an editor over the top of the retime it just filed, which
                    // is the same thing it does for a block's fields.
                    if (coverageDrag.claimClick()) return;
                    // One tag control at a time: an untagged draft and an open
                    // editor would be two cards in an eleven-rem column.
                    setCoverageDraft(null);
                    setRetagging(annotation.id);
                  }
                : undefined
            }
            /* Clock in, offset out, because turning one into the other needs
               the wedding's own start time and the track has never been told
               it. The two rules differ on purpose: a start is ambiguous against
               those hours and `offsetForClockTime` settles it with a documented
               cutoff, while an end has to fall after its start and so has
               exactly one reading — see `coverageDurationForEnd`. */
            onRetimeStart={
              onRetimeCoverage
                ? (annotation, clockTime) => {
                    setRetagging(null);
                    const offsetMinutes = offsetForClockTime(startTime, clockTime);
                    if (offsetMinutes === null) return;
                    // The end holds still, which is what typing a *start*
                    // means. Leaving the length alone instead would move both
                    // edges, and a planner who corrected an arrival time would
                    // find the departure had moved with it.
                    const end = annotation.offsetMinutes + annotation.durationMinutes;
                    retimeCoverage(annotation, {
                      offsetMinutes,
                      durationMinutes: end - offsetMinutes,
                    });
                  }
                : undefined
            }
            onRetimeEnd={
              onRetimeCoverage
                ? (annotation, clockTime) => {
                    setRetagging(null);
                    const durationMinutes = coverageDurationForEnd(
                      startTime,
                      annotation.offsetMinutes,
                      clockTime,
                    );
                    if (durationMinutes === null) return;
                    retimeCoverage(annotation, { durationMinutes });
                  }
                : undefined
            }
            onRetag={
              onRetagCoverage
                ? (annotation, tags) => {
                    setRetagging(null);
                    onRetagCoverage(annotation, tags);
                  }
                : undefined
            }
            onRemove={
              onRemoveCoverage
                ? (annotation) => {
                    setRetagging(null);
                    onRemoveCoverage(annotation);
                  }
                : undefined
            }
            onCancel={() => setRetagging(null)}
            onPlace={
              onPlaceCoverage && coverageDraft
                ? (tags) => {
                    setCoverageDraft(null);
                    onPlaceCoverage({ ...tags, ...coverageDraft });
                  }
                : undefined
            }
            onCancelDraft={() => setCoverageDraft(null)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * How tall a draft's card is drawn, from the minutes it currently claims —
 * cut at the foot of the day, like every other box on this axis.
 *
 * The cut is what makes an after-party creatable. A draft opened at 11:30 PM
 * and pulled past midnight is longer than the canvas has room for, and drawn at
 * its true height it leaves the canvas entirely: the bottom edge the planner is
 * pulling, and the plate reading out the new length, are both on the card. The
 * length it *posts* is the full one — the clip is a drawing, not a limit.
 */
function draftHeight(scale: TimelineLayout<TimelineItemView>, drawn: Draft): number {
  const { height } = clippedBox(
    scale,
    drawn.offsetMinutes,
    drawn.offsetMinutes + drawn.durationMinutes,
    scale.totalHeight,
  );
  return Math.max(DRAFT_CARD_PX, height);
}

/**
 * Something new, at the minutes it was asked for, waiting only for its name.
 *
 * The label is the one field, and it is focused: a click on empty space is the
 * planner saying *when*, and the one thing the axis cannot infer from that is
 * *what*. The note is the block's own field once it exists, which is the whole
 * point of there being no form.
 *
 * The length is the exception, and it is not a field. A draft is drawn at a
 * length and that length is dragged, because the alternative is a planner
 * naming a thing, watching it appear at a length nobody chose, and editing it
 * — three steps for one decision they had already made with their hand on the
 * mouse. So the card is the shape the item is about to have, and it has the
 * same three grab targets a real block has.
 *
 * `ProvisionalCapsule` in `CoverageTrack.tsx` is the track's answer to the same
 * problem and reaches it from the other end: a window's times come out of the
 * sweep that drew it, so what its provisional card is still missing is the tag,
 * and it is not resizable because the gesture that made it already said how
 * long it was. Here the gesture was a click, which says only when.
 *
 * Opaque card rather than the committed block's `bg-primary/10` wash: this is
 * floating over a day it has not joined yet, and a block's own fill would say
 * it had. The ring is the one the blocks wear, at full strength.
 */
function NewItemDraft({
  top,
  height,
  left,
  right,
  clock,
  durationMinutes,
  readout,
  grab,
  onCreate,
  onCancel,
}: {
  top: number;
  height: number;
  /** Where the lane area starts — see `LANES_LEFT_REM`. */
  left: string;
  /** And where it stops, which is the coverage track's edge — see `TRACK_REM`. */
  right: string;
  clock: string;
  durationMinutes: number;
  /** What the drag is saying about itself, or null when nothing is held. */
  readout: TimelineReadout | null;
  grab: Omit<TimelineGrabHandlers, 'onPointerDown'> & {
    onPointerDown: (kind: DragKind, event: ReactPointerEvent<HTMLElement>) => void;
  };
  onCreate: (label: string) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const spent = useRef(false);
  const leave = (commit: boolean) => {
    if (spent.current) return;
    const named = label.trim();
    // A draft nobody named is a click the planner thought better of, not an
    // item called nothing.
    if (commit && named === '') {
      spent.current = true;
      onCancel();
      return;
    }
    spent.current = true;
    if (commit) onCreate(named);
    else onCancel();
  };

  /**
   * A press on a grab target must not move focus.
   *
   * Blur is what commits a draft — clicking away is how a planner says "yes,
   * that one" — so a resize that blurred the field would create the item it
   * was in the middle of sizing, and the gesture would end by making itself
   * impossible. Preventing the default on mousedown is the whole of the fix:
   * the caret stays where the planner left it and the drag runs underneath.
   */
  const keepFocus = (event: ReactMouseEvent<HTMLElement>) => event.preventDefault();

  const pointers = {
    onPointerMove: grab.onPointerMove,
    onPointerUp: grab.onPointerUp,
    onPointerCancel: grab.onPointerCancel,
    onMouseDown: keepFocus,
  };

  return (
    <div
      data-timeline-draft
      className="pointer-events-auto absolute z-20"
      style={{ top, height, left, right }}
    >
      {readout && <ReadoutPlate readout={readout} />}

      {/* The card *is* the body target. The label row sits over it and takes
          the pointer back only for the field itself, so a press on the clock,
          the length, or the padding around them moves the draft, and a press
          in the field places a caret. Stacking does that on its own — the row
          is not an ancestor of this element, so nothing it receives ever
          reaches here. */}
      <div
        data-timeline-grab="body"
        className="absolute inset-0 cursor-grab touch-none rounded-xl bg-card shadow-[0_8px_28px_rgba(49,51,47,0.16)] ring-1 ring-inset ring-primary/75 active:cursor-grabbing"
        onPointerDown={(event) => grab.onPointerDown('body', event)}
        {...pointers}
      />

      {/* Inset by a strip's worth top and bottom, so neither resize target is
          ever laid over the input. An eight-pixel strip over a text field is a
          caret the planner cannot place — the rule the blocks already keep for
          a field that is open, applied to the one field that always is. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-2 px-1.5"
        style={{ paddingTop: EDGE_PX, paddingBottom: EDGE_PX }}
      >
        <span className="shrink-0 pl-1 text-xs tabular-nums">
          <span className="font-medium">{clock}</span>
          {/* Said out loud because nobody chose it. A default the planner
              cannot see is a length they find out about after the fact. */}
          <span className="text-foreground/75"> · {formatDuration(durationMinutes)}</span>
        </span>
        <Input
          autoFocus
          aria-label="What happens"
          className="pointer-events-auto h-7 min-w-0 flex-1 rounded-lg px-2 text-xs"
          maxLength={120}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => leave(true)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            leave(e.key === 'Enter');
          }}
        />
      </div>

      {/* Last, so they read over the card and the row both. The grip is on,
          which is this draft alone among the three resizable things on the
          axis: its length has never been chosen by anyone. */}
      <EdgeHandles drawn={height} grip onGrab={grab.onPointerDown} handlers={pointers} />
    </div>
  );
}

/**
 * Lanes, then the one case lanes cannot reach.
 *
 * `layout` assigns lanes from real intervals, which is right: an item that
 * merely touches the one above it does not overlap it, and reading touching as
 * overlapping splits every adjacent pair into columns. But a zero-length item
 * is a *point*, and two points at the same minute do not overlap under any
 * interval rule — so both get lane 0 and one is drawn exactly on top of the
 * other. A hidden block is the single thing lanes exist to prevent, and it is
 * cheaper and more honest to split the lane here than to give a moment a
 * fictitious length in the geometry.
 *
 * Only exact coincidence is handled. Two moments a minute apart are four pixels
 * apart and their plates still overlap; that is the general short-block problem
 * and not something a lane can answer.
 */
function place(axis: TimelineLayout<TimelineItemView>) {
  const coincident = new Map<string, number>();
  for (const block of axis.blocks) {
    if (block.effectiveDurationMinutes !== 0) continue;
    const key = `${block.top}:${block.lane}`;
    coincident.set(key, (coincident.get(key) ?? 0) + 1);
  }

  const taken = new Map<string, number>();
  return axis.blocks.map((block) => {
    const key = `${block.top}:${block.lane}`;
    const share = block.effectiveDurationMinutes === 0 ? (coincident.get(key) ?? 1) : 1;
    const index = share > 1 ? (taken.get(key) ?? 0) : 0;
    if (share > 1) taken.set(key, index + 1);

    return {
      item: block.item,
      effectiveDurationMinutes: block.effectiveDurationMinutes,
      placement: {
        top: block.top,
        height: block.height,
        left: percent(((block.lane + index / share) / block.laneCount) * 100),
        width: percent(100 / (block.laneCount * share)),
      },
    };
  });
}

/**
 * The blocks to draw: the day's own, plus the one under the pointer when the
 * gesture has carried it off this day.
 *
 * A drag that crosses midnight moves the *view*, and the view is one calendar
 * day — so for the rest of that gesture the block being dragged is an item
 * whose stored offset is on another date, which `layout` quite rightly leaves
 * out. Leaving this one out with them unmounts the element the pointer is
 * captured on, and the gesture then dies in silence halfway through: the
 * planner is still holding the block, every further move goes to a detached
 * node, and the `pointerup` that would have filed the PATCH is never heard. It
 * read as a drag that simply does nothing.
 *
 * Placed at nothing in particular, because `dragPlacement` overrides every
 * number on it at the call site below — this entry exists to keep an element
 * mounted, not to say where anything is. Full width and lane 0 for the same
 * reason: it is drawn above the day it has arrived on, `z-10` while dragging,
 * and the lanes beneath it hold still for the length of the gesture exactly as
 * they do for every other drag.
 */
function carried(
  axis: TimelineLayout<TimelineItemView, AxisCoverage>,
  drag: TimelineDragState | null,
  byId: Map<string, TimelineItemView>,
) {
  const placed = place(axis);
  if (!drag || placed.some((p) => p.item.id === drag.id)) return placed;
  const item = byId.get(drag.id);
  if (!item) return placed;
  return [
    ...placed,
    {
      item,
      effectiveDurationMinutes: drag.durationMinutes,
      placement: { top: 0, height: MIN_BLOCK_PX, left: '0%', width: '100%' },
    },
  ];
}

/**
 * Where the block under the pointer is drawn, which is not where `layout` put it.
 *
 * The rest of the day holds still for the length of a gesture — lanes do not
 * re-shuffle and gaps do not re-collapse under a moving block, which would
 * change the scale the drag is being measured against and set the whole thing
 * oscillating. Only the dragged block moves, and it moves through the same
 * `topForOffset` everything else was placed with, so it stays on the scale the
 * gutter is drawing.
 */
function dragPlacement(
  scale: TimelineLayout<TimelineItemView>,
  drag: TimelineDragState,
  placement: { left: string; width: string },
) {
  return {
    ...placement,
    // Through `clippedBox`, so a live box obeys the day exactly as a laid-out
    // one does. A bottom edge pulled past midnight used to draw the card at its
    // true height and run it off the foot of the canvas — with the handle being
    // pulled and the readout plate both on it, and auto-scroll already stopped
    // at the end of the day. The gesture is differential, so a handle resting
    // on the seam goes on lengthening the block by the distance the pointer
    // moves: what is clipped is the drawing, never the length.
    ...clippedBox(
      scale,
      drag.offsetMinutes,
      drag.offsetMinutes + drag.durationMinutes,
      scale.totalHeight,
    ),
  };
}

/**
 * Where a span the planner is drawing sits, in pixels.
 *
 * Through `topForOffset` twice rather than a height in minutes times a rate:
 * the two conversions are the ones the gutter and the blocks were drawn with,
 * so a provisional capsule lines up with the hour beside it. Floored at
 * `MIN_BLOCK_PX` exactly as a committed capsule is — a sweep is five minutes
 * long before it is anything else, and ten pixels of it is not visible enough
 * to tell the planner the gesture has started.
 */
function spanPlacement(
  scale: TimelineLayout<TimelineItemView>,
  span: CoverageSpan,
): { top: number; height: number } {
  // And cut at the day, like the capsules `layout` places and like a block
  // being dragged: a photographer's window swept toward midnight has the same
  // reason not to leave the canvas it is being drawn on.
  return clippedBox(
    scale,
    span.offsetMinutes,
    span.offsetMinutes + span.durationMinutes,
    scale.totalHeight,
  );
}

/**
 * Where the capsule under the pointer is drawn, which is not where `layout`
 * put it — the rail's half of what `dragPlacement` does for a block.
 *
 * The rest of the rail holds still for the length of a gesture: lanes do not
 * re-shuffle under a moving capsule, which would change the column the drag is
 * being measured in and set it jumping sideways mid-drag. Only the capsule
 * under the pointer moves, and it moves on the same scale as everything else.
 */
function coverageDragPlacement(
  scale: TimelineLayout<TimelineItemView>,
  drag: TimelineDragState | null,
  startTime: string | null,
): { id: string; top: number; height: number; readout: TimelineReadout } | null {
  if (!drag) return null;
  return {
    id: drag.id,
    // Where it is and what that means, worked out together. A capsule prints
    // its stored hours while it is dragged, so the plate is the only thing
    // saying where the window will actually land.
    readout: readoutFor(drag, startTime),
    ...spanPlacement(scale, {
      offsetMinutes: drag.offsetMinutes,
      durationMinutes: drag.durationMinutes,
    }),
  };
}

/** `'6:15 PM'`, or an empty string when there is no start to be an offset from. */
function clockAt(startTime: string | null, offsetMinutes: number): string {
  const clock = clockTimeForOffset(startTime, offsetMinutes);
  return clock === null ? '' : (formatTimeOfDay(clock) ?? clock);
}

/**
 * How far, and which way.
 *
 * Signed and in words, because "15" alone leaves the planner working out
 * whether the day got later or earlier from the direction their hand moved.
 * A true minus sign rather than a hyphen: these figures are set tabular, and a
 * hyphen is the one glyph in the string that is not.
 */
function deltaText(minutes: number): string {
  if (minutes === 0) return 'No change';
  return `${minutes > 0 ? '+' : '−'}${formatDuration(Math.abs(minutes))}`;
}

/**
 * What the block says while it is being dragged.
 *
 * A body drag is about position, so the delta is the time moved. An edge drag
 * is about length, so it is the minutes gained or lost, with the old length and
 * the new one stated either side of an arrow.
 */
function readoutFor(drag: TimelineDragState, startTime: string | null): TimelineReadout {
  const body = drag.kind === 'body';
  return {
    start: clockAt(startTime, drag.offsetMinutes),
    // A moment starts and ends at the same minute; printing it twice reads as
    // a bug rather than as a point in time.
    end:
      drag.durationMinutes === 0
        ? null
        : clockAt(startTime, drag.offsetMinutes + drag.durationMinutes),
    length: body
      ? null
      : `${formatDuration(drag.fromDurationMinutes)} → ${formatDuration(drag.durationMinutes)}`,
    delta: deltaText(
      body
        ? drag.offsetMinutes - drag.fromOffsetMinutes
        : drag.durationMinutes - drag.fromDurationMinutes,
    ),
  };
}

/** Trimmed, so a third of a lane is `33.3333%` and not seventeen digits of it. */
function percent(value: number): string {
  return `${Number(value.toFixed(4))}%`;
}

interface HourTick {
  offsetMinutes: number;
  top: number;
  label: string;
}

/**
 * The hours the wedding itself keeps, drawn across the day.
 *
 * Both from one component on purpose: they are a pair a planner reads
 * together, and two copies of this markup would eventually disagree about a
 * colour or a height and stop looking like the same kind of statement.
 *
 * `bg-primary/70` on the rule is 3.25:1 on the card — the floor for a graphic
 * carrying meaning, which these are. The axis is the whole calendar date, so
 * the stretches outside this pair are day too, not margin: these two lines are
 * what separates the hours the couple booked from the morning and the small
 * hours around them.
 */
function Marker({
  kind,
  top,
  left,
  trackRem,
  gutterRem,
  label,
}: {
  kind: 'start' | 'end';
  top: number;
  /** Where the lane area starts — see `LANES_LEFT_REM`. */
  left: string;
  /** How wide the coverage track is, for the continuation past the pill. */
  trackRem: number;
  /** And the gutter beyond it, which the rule must stop short of. */
  gutterRem: number;
  label: string;
}) {
  return (
    <div
      data-timeline-marker={kind}
      /* To the canvas's right edge, with the gutter held clear by a spacer at
         the end. The pill lands on the day's right edge because the leading
         rule is the only `flex-1` in the row — everything after it is a fixed
         width. */
      /* No `gap`, and margins on the pill instead. A gap applies between every
         pair, so it would push the trailing rule half a rem past the track's
         closing edge and into the gutter — the same arithmetic that printed the
         right-hand hour labels on top of the capsules. With explicit margins
         the trailing rule is exactly `trackRem` wide and lands exactly on the
         column. */
      className="pointer-events-none absolute right-0 flex -translate-y-1/2 items-center"
      style={{ top, left }}
    >
      <span className="h-px flex-1 bg-primary/70" />
      <span className="label-caps mx-2 rounded-full bg-primary px-2 py-0.5 text-[0.625rem] text-primary-foreground">
        {label}
      </span>
      {/* The same rule again, carrying on across the coverage track.
      
          The pill stays at the day's right edge — it names a fact about the
          couple's day and printing it over the capsules would be that fact
          drawn inside a column that is not the day. But the *line* belongs in
          both columns, because the question it answers is asked of coverage
          more than of anything else: a photographer's window running past the
          end of the night is the overrun a planner needs to see, and with the
          line stopping at the seam there was nothing in the track to see it
          against. Rule, pill, rule — one marker crossing two columns. */}
      <span
        className="h-px shrink-0 bg-primary/70"
        style={{ width: `${trackRem}rem` }}
        aria-hidden="true"
      />
      {/* The hour gutter beyond the track, left empty: the marker's line stops
          on the track's closing edge, where every other rule on this axis
          stops. */}
      <span className="shrink-0" style={{ width: `${gutterRem}rem` }} aria-hidden="true" />
    </div>
  );
}

/**
 * The wall-clock hours the gutter prints, and where each falls.
 *
 * Every hour the day passes through, `12 AM` at each edge included. It used to
 * take an `except` set, because midnight *inside* the axis was a dated divider
 * and `12 AM` beside `Jun 13` is the same fact twice in a 52px gutter. One day
 * at a time has no interior midnight, the dividers are gone, and the date is
 * named once above the canvas by `TimelineDateSelector` instead.
 *
 * Positioned with `topForOffset` rather than by multiplying here. One
 * conversion, used by the gutter and the blocks alike: two that disagreed by a
 * pixel would draw every block at a time its own ruler denies.
 */
function hourTicks(axis: TimelineLayout<TimelineItemView>, startTime: string | null): HourTick[] {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  if (!start) return [];

  const dayMinutes = minutesSinceMidnight(start);
  // The first offset whose wall clock is on the hour, at or after the top.
  const first =
    axis.startOffsetMinutes + mod(60 - mod(dayMinutes + axis.startOffsetMinutes, 60), 60);

  const ticks: HourTick[] = [];
  for (let offset = first; offset <= axis.endOffsetMinutes; offset += 60) {
    const clock = clockTimeForOffset(startTime, offset);
    if (!clock) continue;
    // `topForOffset`, not a second copy of the conversion: a gutter that
    // disagreed with the blocks by even a pixel would be worse than no gutter.
    ticks.push({ offsetMinutes: offset, top: topForOffset(axis, offset), label: hourLabel(clock) });
  }
  return ticks;
}

function mod(value: number, by: number): number {
  return ((value % by) + by) % by;
}

/** `'18:00'` → `'6 PM'`. The gutter is hours; `:00` on every one is noise. */
function hourLabel(clockTime: string): string {
  const time = parseTimeOfDay(clockTime);
  if (!time) return clockTime;
  const hour = time.hour % 12 === 0 ? 12 : time.hour % 12;
  return `${hour} ${time.hour < 12 ? 'AM' : 'PM'}`;
}
